import React, { useEffect, useState } from "react";
import api from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";
import { formatInUserTimezone } from "../../utils/timezone";
import EmailTemplateEditor from "./EmailTemplateEditor";
import EmailTemplatePreview from "./EmailTemplatePreview";

function HealthStat({ label, value, tone }) {
  return (
    <div className={`health-stat health-tone-${tone || "neutral"}`}>
      <div className="health-stat-value">{value}</div>
      <div className="health-stat-label">{label}</div>
    </div>
  );
}

function AdminNotifications() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState("");
  const [health, setHealth] = useState(null);

  const [showPreview, setShowPreview] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [form, setForm] = useState({
    email_template_subject: "",
    email_template_body: "",
    email_enabled: true,
    auto_log_to_activity: true,
    digest_frequency: "once_daily",
    default_reminder_time: "09:00",
    timezone_mode: "rep_local",
  });

  useEffect(() => {
    let cancelled = false;
    api
      .getAdminNotificationSettings()
      .then((data) => {
        if (cancelled || !data) return;
        setForm({
          email_template_subject: data.email_template_subject || "",
          email_template_body: data.email_template_body || "",
          email_enabled: data.email_enabled === 1,
          auto_log_to_activity: data.auto_log_to_activity === 1,
          digest_frequency: data.digest_frequency || "once_daily",
          default_reminder_time: data.default_reminder_time || "09:00",
          timezone_mode: data.timezone_mode || "rep_local",
        });
      })
      .catch((e) =>
        setError(e?.response?.data?.error || "Failed to load settings"),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getAdminNotificationHealth()
      .then((data) => {
        if (cancelled) return;
        setHealth(data || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setHealthError(
          e?.response?.data?.error || "Failed to load system health",
        );
      })
      .finally(() => !cancelled && setHealthLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        email_template_subject: form.email_template_subject,
        email_template_body: form.email_template_body,
        email_enabled: form.email_enabled,
        auto_log_to_activity: form.auto_log_to_activity,
        digest_frequency: form.digest_frequency,
        default_reminder_time: form.default_reminder_time,
        timezone_mode: form.timezone_mode,
      };
      const fresh = await api.updateAdminNotificationSettings(payload);
      setForm((prev) => ({ ...prev, ...fresh }));
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleEditorChange = ({ subject, body }) => {
    setForm((prev) => ({
      ...prev,
      email_template_subject: subject,
      email_template_body: body,
    }));
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setSendResult(null);
    try {
      await api.sendTestNotificationEmail({
        subject: form.email_template_subject,
        body: form.email_template_body,
      });
      setSendResult({ ok: true });
    } catch (e) {
      setSendResult({
        error:
          e?.response?.data?.error ||
          e?.error ||
          "Failed to send test email",
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading notification settings..." />;
  }

  const lastFiredLabel = health?.lastJobFiredAt
    ? formatInUserTimezone(health.lastJobFiredAt)
    : "Never";

  return (
    <div className="admin-notifications">
      <div className="management-header">
        <div>
          <h2>Notification Settings</h2>
          <p className="text-muted">
            Monitor reminder health and control Next Activities reminder
            behavior.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> Save
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: 12 }}>
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}
      {savedAt && !error && (
        <div className="text-muted" style={{ marginBottom: 12, fontSize: 12 }}>
          Saved at {savedAt.toLocaleTimeString()}
        </div>
      )}

      {/* ============ SYSTEM HEALTH ============ */}
      <div className="form-section">
        <div className="form-section-header">
          <span className="section-number">
            <i className="fas fa-heartbeat" />
          </span>
          <div>
            <h3>System Health</h3>
            <p className="section-subtitle">
              Live snapshot of reminder activity in the last 24 hours
            </p>
          </div>
        </div>

        {healthLoading ? (
          <div className="text-muted" style={{ padding: "12px 4px" }}>
            <i className="fas fa-spinner fa-spin" /> Loading health...
          </div>
        ) : healthError ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle" /> {healthError}
          </div>
        ) : health ? (
          <>
            <div className="health-grid">
              <HealthStat
                label="Reminders scheduled next 24h"
                value={health.scheduledNext24h}
                tone="info"
              />
              <HealthStat
                label="Reminders fired last 24h"
                value={health.firedLast24h}
                tone="success"
              />
              <HealthStat
                label="Failed emails last 24h"
                value={health.failedEmailsLast24h}
                tone={health.failedEmailsLast24h > 0 ? "danger" : "success"}
              />
              <HealthStat
                label="Digests sent last 24h"
                value={health.digestsLast24h}
                tone="info"
              />
              <HealthStat
                label="Active Next Activities"
                value={health.activeNextActivities}
                tone="neutral"
              />
              <HealthStat
                label="Overdue Next Activities"
                value={health.overdueNextActivities}
                tone={health.overdueNextActivities > 0 ? "warning" : "success"}
              />
            </div>
            <div className="health-footer">
              <i className="fas fa-clock" />
              Last reminder job run: <strong>{lastFiredLabel}</strong>
            </div>
          </>
        ) : null}
      </div>

      {/* ============ GLOBAL TOGGLES ============ */}
      <div className="form-section">
        <div className="form-section-header">
          <span className="section-number">1</span>
          <h3>Global Toggles</h3>
        </div>

        <label className="next-activity-check">
          <input
            type="checkbox"
            checked={form.email_enabled}
            onChange={(e) =>
              setForm({ ...form, email_enabled: e.target.checked })
            }
          />
          <span>Email reminders enabled</span>
        </label>

        <label className="next-activity-check">
          <input
            type="checkbox"
            checked={form.auto_log_to_activity}
            onChange={(e) =>
              setForm({ ...form, auto_log_to_activity: e.target.checked })
            }
          />
          <span>Auto-log completed Next Activities into the Activity Log</span>
        </label>

        <div className="form-row">
          <div className="form-group">
            <label>Digest frequency</label>
            <select
              className="form-control"
              value={form.digest_frequency}
              onChange={(e) =>
                setForm({ ...form, digest_frequency: e.target.value })
              }
            >
              <option value="once_daily">Once daily</option>
              <option value="twice_daily">Twice daily</option>
              <option value="off">Off</option>
            </select>
          </div>
          <div className="form-group">
            <label>Default reminder time</label>
            <input
              type="time"
              className="form-control"
              value={form.default_reminder_time}
              onChange={(e) =>
                setForm({ ...form, default_reminder_time: e.target.value })
              }
            />
          </div>
        </div>

        <div className="form-group">
          <label>Timezone mode</label>
          <select
            className="form-control"
            value={form.timezone_mode}
            onChange={(e) =>
              setForm({ ...form, timezone_mode: e.target.value })
            }
          >
            <option value="rep_local">Rep local</option>
            <option value="utc">UTC</option>
          </select>
        </div>
      </div>

      {/* ============ EMAIL TEMPLATE ============ */}
      <div className="form-section">
        <div className="form-section-header">
          <span className="section-number">2</span>
          <h3>Email Template</h3>
        </div>

        <EmailTemplateEditor
          subject={form.email_template_subject}
          body={form.email_template_body}
          onChange={handleEditorChange}
          disabled={saving}
        />

        <div className="email-template-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setSendResult(null);
              setShowPreview(true);
            }}
          >
            <i className="fas fa-eye" /> Preview with sample data
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleSendTest}
            disabled={sendingTest}
          >
            {sendingTest ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane" /> Send test to myself
              </>
            )}
          </button>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> Save Settings
            </>
          )}
        </button>
      </div>

      {showPreview && (
        <EmailTemplatePreview
          subject={form.email_template_subject}
          body={form.email_template_body}
          onClose={() => {
            setShowPreview(false);
            setSendResult(null);
          }}
          onSendTest={handleSendTest}
          sending={sendingTest}
          sendResult={sendResult}
        />
      )}
    </div>
  );
}

export default AdminNotifications;